import { useGlobalStore } from '@/store';
import { UsePrintFeatureParams, UsePrintFeatureReturn } from './types';
import { useReactToPrint } from 'react-to-print';

/**
 * Custom hook to manage printing functionality across the application. It integrates with the global store
 * to manage the printing state and uses the react-to-print library to handle the print action. This hook
 * can be used in any component that requires printing capabilities, providing a consistent interface for
 * triggering prints and managing print state.
 */
export const usePrintFeature = ({
  contentRef,
  documentTitle,
  pageStyle,
  onClick,
}: UsePrintFeatureParams): UsePrintFeatureReturn => {
  // ============================================================
  // Global Store Access
  // ============================================================
  const { isPrinting, setIsPrinting } = useGlobalStore();

  // ============================================================
  // Print Handler
  // ============================================================
  /**
   * Handle the click event for the print button. It sets the global printing state to true
   * and then calls the provided onClick handler if it exists. This allows the application
   * to manage the printing state globally, which can be useful hiding or showing content.
   */
  const handlePrint = useReactToPrint({
    contentRef, // The content to be printed, passed as a ref
    documentTitle, // The title for the print document
    pageStyle, // Custom page style for printing
    onBeforePrint: () => {
      onClick?.(); // Call the provided onClick handler if it exists
      setIsPrinting(true); // Set the global printing state to true
      return Promise.resolve(); // Return a resolved promise to ensure the print action proceeds
    },
    onAfterPrint: () => {
      setIsPrinting(false); // Reset the global printing state to false after printing is done
    },
    onPrintError: (errorLocation, error) => {
      console.error(`Print error occurred during ${errorLocation}:`, error);
      setIsPrinting(false); // Ensure printing state is reset even if there's an error
    },
  });

  // ============================================================
  // Return the print handler and printing state
  // ============================================================
  return {
    handlePrint,
    isPrinting,
  };
};
